import React, { useState, useCallback, useRef, useEffect } from 'react';
import Map, { NavigationControl, ScaleControl, Popup } from 'react-map-gl/maplibre';
import { MaplibreTerradrawControl } from '@watergis/maplibre-gl-terradraw';
import '@watergis/maplibre-gl-terradraw/dist/maplibre-gl-terradraw.css';
import { getViewModeStyle } from '../lib/map-styles';
import { mapTransformRequest } from '../lib/map-auth';
import { useTheme } from '../contexts/ThemeContext';
import { safeRemoveControl } from '../lib/map-teardown';

/**
 * End-to-end tests (frontend/e2e) read the live MapLibre instance as `window.__e2eMap` to check
 * layers and draw order. Only set when the test harness put `e2e` in localStorage.
 */
export function exposeMapForE2e(map, win = typeof window !== 'undefined' ? window : undefined) {
  try {
    if (win && win.localStorage.getItem('e2e')) win.__e2eMap = map;
  } catch { /* storage blocked */ }
}

/**
 * SurveyMap — MapLibre GL JS map engine for Ultimate-GIS
 * 
 * Props:
 *   viewMode        - 'map' | 'satellite' | 'topo' | 'plan'
 *   initialCenter   - { lat, lon }
 *   initialZoom     - number (default 12)
 *   children        - MapLibre Source/Layer components
 *   onViewportChange - callback({ latitude, longitude, zoom, bearing, pitch })
 *   onMapClick       - callback(event)
 *   onMouseMove      - callback(event)
 *   onContextMenu    - callback(event): right-click on the map (the handler decides whether to preventDefault)
 *   cursor           - CSS cursor string
 *   interactiveLayerIds - string[] for queryRenderedFeatures
 */
export default function SurveyMap({
  viewMode = 'map',
  initialCenter = { lat: 27.9478, lon: -82.4584 }, // Tampa, FL (FDOT site)
  initialZoom = 13,
  children,
  onViewportChange,
  onMapClick,
  onDblClick,
  onMouseMove,
  onContextMenu,
  cursor = 'default',
  interactiveLayerIds = [],
  popupInfo,
  onPopupClose,
  onMapRef,
  onDrawReady,
  style: containerStyle = { width: '100%', height: '100%' },
}) {
  const mapRef = useRef(null);
  const drawControlRef = useRef(null);
  const drawMapRef = useRef(null); // the map the draw control was added to (mapRef is cleared before cleanups run)

  // Expose map ref to parent (for PNG export, georeferencing, etc.)
  useEffect(() => {
    if (onMapRef) onMapRef(mapRef);
  }, [onMapRef]);

  // Terra Draw — professional drawing with vertex editing, select, undo/redo
  const handleMapLoad = useCallback(() => {
    if (drawControlRef.current || !mapRef.current) return;
    const map = mapRef.current.getMap();
    exposeMapForE2e(map);
    const control = new MaplibreTerradrawControl({
      modes: [
        'render', 'point', 'linestring', 'polygon', 'rectangle', 'circle',
        'freehand', 'select', 'delete-selection', 'delete', 'download',
      ],
      open: false,
    });
    map.addControl(control, 'top-right');
    drawControlRef.current = control;
    drawMapRef.current = map;
    if (onDrawReady) onDrawReady(control);
  }, [onDrawReady]);
  // Detach the draw control before react-map-gl removes the map; its own teardown throws when
  // the style is mid-reload (V.20) and that used to blank the whole app.
  useEffect(() => () => {
    const control = drawControlRef.current;
    drawControlRef.current = null;
    if (!control) return;
    try {
      const map = drawMapRef.current;
      drawMapRef.current = null;
      safeRemoveControl(map, control);
    } catch { /* map already gone */ }
  }, []);
  const [dragging, setDragging] = useState(false);
  const [viewState, setViewState] = useState({
    longitude: initialCenter.lon ?? -70.15,
    latitude: initialCenter.lat ?? 41.99,
    zoom: initialZoom ?? 10,
    bearing: 0,
    pitch: 0,
  });

  // Update view when initialCenter/initialZoom props change. react-map-gl applies a
  // controlled view change without firing onMove, so report it to onViewportChange here,
  // or the status bar's scale/zoom would stay on the old view (e.g. after an import fit).
  const onViewportChangeRef = useRef(onViewportChange);
  onViewportChangeRef.current = onViewportChange;
  const viewStateRef = useRef(viewState);
  viewStateRef.current = viewState;
  useEffect(() => {
    if (initialCenter.lat != null && initialCenter.lon != null) {
      const next = {
        ...viewStateRef.current,
        longitude: initialCenter.lon,
        latitude: initialCenter.lat,
        zoom: initialZoom,
      };
      setViewState(prev => ({ ...prev, longitude: next.longitude, latitude: next.latitude, zoom: next.zoom }));
      if (onViewportChangeRef.current) onViewportChangeRef.current(next);
    }
  }, [initialCenter.lat, initialCenter.lon, initialZoom]);

  const handleMove = useCallback((evt) => {
    setViewState(evt.viewState);
    if (onViewportChange) {
      onViewportChange(evt.viewState);
    }
  }, [onViewportChange]);

  const handleClick = useCallback((evt) => {
    if (onMapClick) {
      onMapClick(evt);
    }
  }, [onMapClick]);

  const handleDblClick = useCallback((evt) => {
    if (onDblClick) {
      evt.preventDefault();
      onDblClick(evt);
    }
  }, [onDblClick]);

  const handleMouseMove = useCallback((evt) => {
    if (onMouseMove) {
      onMouseMove(evt);
    }
  }, [onMouseMove]);

  // Get map style based on view mode
  const { theme } = useTheme();
  const mapStyle = getViewModeStyle(viewMode, theme);

  // Ensure valid numeric values for MapLibre (prevents Invalid LngLat errors)
  const safeViewState = {
    ...viewState,
    longitude: typeof viewState.longitude === 'number' ? viewState.longitude : -70.15,
    latitude: typeof viewState.latitude === 'number' ? viewState.latitude : 41.99,
    zoom: typeof viewState.zoom === 'number' ? viewState.zoom : 10,
  };

  return (
    <div style={{ position: 'relative', ...containerStyle }}>
      <Map
        ref={mapRef}
        {...safeViewState}
        onLoad={handleMapLoad}
        onMove={handleMove}
        onClick={handleClick}
        onDblClick={handleDblClick}
        onMouseMove={handleMouseMove}
        onContextMenu={onContextMenu}
        mapStyle={mapStyle}
        style={{ width: '100%', height: '100%' }}
        cursor={cursor === 'grab' && dragging ? 'grabbing' : cursor}
        onDragStart={() => setDragging(true)}
        onDragEnd={() => setDragging(false)}
        interactiveLayerIds={interactiveLayerIds}
        attributionControl={{ compact: true }}
        preserveDrawingBuffer={true}
        // Adds the bearer token to our raster and vector tile requests only (lib/map-auth.js)
        transformRequest={mapTransformRequest}
      >
        <NavigationControl position="top-right" showCompass={true} />
        <ScaleControl position="bottom-left" maxWidth={200} unit="metric" />

        {children}

        {/* Popup */}
        {popupInfo && (
          <Popup
            longitude={popupInfo.longitude}
            latitude={popupInfo.latitude}
            onClose={onPopupClose}
            closeOnClick={false}
            // No fixed anchor: MapLibre flips the popup to stay inside the map near an edge.
            offset={10}
            maxWidth="320px"
          >
            {popupInfo.content}
          </Popup>
        )}
      </Map>

      {/* Zoom percentage overlay */}
      <div style={{
        position: 'absolute',
        bottom: 36,
        right: 10,
        background: 'rgba(13,17,23,0.85)',
        border: '1px solid rgba(255,255,255,0.07)',
        borderRadius: 6,
        padding: '3px 8px',
        fontSize: 11,
        color: '#c9d1d9',
        pointerEvents: 'none',
        zIndex: 5,
      }}>
        {Math.round(Math.pow(2, viewState.zoom - 10) * 100)}%
      </div>

    </div>
  );
}
